"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import { BodyXl } from "@/domains/design/components/body-xl"
import { CloseIcon } from "@/domains/icons"
import { cn } from "@/domains/library"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

type DialogPortalProps = DialogPrimitive.DialogPortalProps & {
  className?: string
}

const DialogPortal = ({ children, ...props }: DialogPortalProps) => (
  <DialogPrimitive.Portal {...props}>
    <div className="fixed inset-0 z-50 flex items-start justify-center sm:items-center">{children}</div>
  </DialogPrimitive.Portal>
)
DialogPortal.displayName = DialogPrimitive.Portal.displayName

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-p500 opacity-80 transition-all duration-100 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in",
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    title: string
    description?: string
  }
>(({ className, children, title, description, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "dialog-content",
        "fixed z-50",
        "w-full",
        "grid gap-4",
        "rounded-b-lg sm:rounded-lg",
        "bg-p400",
        "shadow-lg",
        "animate-in",
        "sm:max-w-2xl",
        "sm:zoom-in-90",
        "data-[state=open]:fade-in-90",
        "data-[state=open]:slide-in-from-bottom-10",
        "data-[state=open]:sm:slide-in-from-bottom-0",
        "px-3 py-4 sm:px-5 md:py-5",
        className,
      )}
      {...props}
    >
      <div className="mb-1 flex items-center justify-between md:mb-4">
        <DialogTitle>
          <BodyXl className="text-t100">{title}</BodyXl>
        </DialogTitle>
        <DialogPrimitive.Close
          className={cn(
            "here-we-go",
            "rounded-sm",
            "bg-p200-shade hover:bg-p200-light",
            "transition-all duration-300 ease-in-out",
            "h-6 w-6",
            "text-t100/60 hover:text-t100/80",
            "disabled:pointer-events-none",
            // "opacity-70 hover:opacity-100",
          )}
        >
          <CloseIcon size={24} className="z-100" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </div>
      <DialogDescription className="sr-only">{description ?? title}</DialogDescription>
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("dialog-footer flex flex-row justify-center sm:justify-end sm:space-x-2", className)} {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn("text-lg text-t100", className)} {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-t300", className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger }
